/**
 * formatTextExecutor — `format_text` sur la cible unique : un passage, un
 * texte exact dans tout l'acte (#1947), une liste de paragraphes, une plage,
 * tout l'acte (#1863, spec #1858). Sept
 * réglages depuis #1864 : gras, italique, souligné, surlignage, couleur,
 * police, taille — chacun se pose et se retire. Depuis #1865, un filtre
 * `onlyWhere` restreint l'écriture au texte qui a déjà une forme donnée. Ce que chaque réglage devient
 * pour l'éditeur, et ce que « conforme » veut dire pour lui, vit dans
 * `reglagesDeTexte.ts`, avec ses mesures.
 *
 * Les règles communes aux écritures de forme — écriture directe, cible résolue
 * au fichier exporté, relecture clôturée sur la révision, pas de garde de
 * portée d'inspection — et ce que l'éditeur fait d'un plan appliqué sont dits
 * une fois, dans l'en-tête de `ecritureDeForme.ts`. Ici : ce qui est propre au
 * texte.
 *
 * **Un plan atomique par lot.** `mutations.apply` avec une étape `format.apply`
 * par paragraphe — par passage sous un filtre `onlyWhere` : une seule entrée
 * d'historique, donc un seul Ctrl+Z défait tout le lot (`mesure-forme-en-lot`).
 * Le reçu, décidé par `recuDEcritureDeForme.ts`, dit par réglage conforme,
 * différent (avec la valeur lue) ou non lisible (`verificationDeForme.ts`).
 *
 * ## Ce qui est mesuré, et dont le code dépend
 *
 * Sonde `tests/ws-mocked/mesure-format-text-cibles.spec.ts` (SuperDoc 2.15.1,
 * 2026-10-02, éditeur en mode suggestion) :
 *
 * - Plusieurs réglages groupés dans l'`inline` d'UNE étape sont tous posés
 *   (gras, italique, souligné, surlignage relus au fichier).
 * - Deux étapes de BLOC sur le même paragraphe font refuser le plan
 *   (`TARGET_CONFLICT`, jeté) : d'où une étape par paragraphe, tous réglages
 *   groupés.
 * - Un paragraphe SANS TEXTE dans le lot fait refuser le plan entier
 *   (`INVALID_TARGET`) : l'aperçu rend `valid:false` et nomme l'étape,
 *   l'application JETTE. Ces paragraphes sont donc écartés de la cible et
 *   comptés (`no_text`) — il n'y a pas de texte à mettre en forme. Ils ne
 *   passent pas par le repli d'un lot refusé (`ecritureDeForme.ts`).
 * - Une étape qui ne change RIEN fait refuser le plan : l'application JETTE
 *   `NO_OP`, alors que l'aperçu l'avait dit valide ; rien n'est écrit (les
 *   étapes déjà jouées sont défaites), aucune entrée d'historique. « Ne change
 *   rien » veut dire : chaque caractère porte déjà la valeur demandée EN FORME
 *   DIRECTE — une valeur portée par un style ou par le défaut du document est
 *   écrite à la main sans refus — ou, pour un retrait, n'a aucune forme directe
 *   à effacer. Une étape dont UN réglage au moins change quelque chose est
 *   acceptée. Les paragraphes où rien ne changerait sont donc laissés hors du
 *   plan (`ecritureSurLEtendue`) et comptés.
 * - Ce que ce refus fait de la RÉVISION du document dépend de la place de
 *   l'étape sans effet. Sauf mention, les gestes cités sont ceux de
 *   `mesure-format-text-police-taille-couleur` ; les deux sondes sont du
 *   2026-10-02 :
 *   plan d'une seule étape (C2 ; N1b de `mesure-format-text-cibles`), étape
 *   sans effet en PREMIER d'un lot (N2), ou lot dont TOUTES les étapes sont
 *   sans effet (N4) → la révision ne bouge pas ; étape sans effet APRÈS
 *   d'autres étapes (N3, au milieu d'un lot de trois ; N2 de
 *   `mesure-format-text-cibles`) → la révision avance (de 2) bien que rien ne
 *   soit écrit. Une révision
 *   qui a bougé ne prouve donc pas une écriture : le reçu le prouve sur le
 *   fichier (`recuDEcritureDeForme.ts`).
 * - ⚠️ Une étape sans effet en DERNIÈRE position d'un lot de deux n'a PAS fait
 *   refuser le plan (N1 : accepté, les deux étapes dites `changed`). Le refus
 *   n'est donc pas garanti ; l'exclusion des paragraphes sans effet reste la
 *   règle, parce qu'elle est sûre dans les deux cas.
 * - L'identifiant décide seul du bloc visé : un `nodeType` qui ne correspond
 *   pas (titre adressé comme paragraphe) est accepté et écrit. L'exécuteur ne
 *   lit donc pas la liste des blocs de l'éditeur pour le connaître.
 *
 * ## Le filtre `onlyWhere` : des passages, adressés par position (#1865)
 *
 * « Passe tout le gras en italique » : les passages sont trouvés par le lecteur
 * de forme (`passagesFiltres.ts`), donc aussi quand la forme vient d'un style
 * nommé, et un paragraphe peut en porter plusieurs. Sonde
 * `tests/ws-mocked/mesure-format-text-only-where.spec.ts` (SuperDoc 2.15.1,
 * 2026-10-02, éditeur en mode suggestion ; la lettre et le numéro renvoient au
 * geste de la sonde) :
 *
 * - Une étape adressée par une sélection (`where:{by:'target'}`) est REFUSÉE
 *   (`CAPABILITY_UNSUPPORTED` : « requires a ref produced by query.match/find
 *   or a single text selector », Q0). Une référence rendue par `query.match`
 *   (`by:'ref'`) ne vaut que dans un plan d'UNE étape (« multi-step mutation
 *   plans cannot use revision-bound ref targets », Q4) et se périme à la
 *   première écriture (`REVISION_MISMATCH`, Q10).
 * - Reste le sélecteur de texte (`by:'select'`), qui accepte une expression
 *   régulière bornée au paragraphe : `^` y est le début du paragraphe, et
 *   l'assertion arrière est admise (Q12). `(?<=^[\s\S]{début})[\s\S]{longueur}`
 *   adresse donc une plage par sa POSITION seule, sans son texte : deux
 *   passages de même texte se distinguent. Compteurs mesurés jusqu'à un début
 *   de 931 (Q12) et une longueur de 1 102 (Q6) ; aucune borne n'a été cherchée
 *   au-delà. Le motif reste court quel que soit le passage, et il le faut :
 *   l'éditeur refuse une expression longue, en ces mots — « regex pattern must
 *   be 1024 characters or fewer. » (`INVALID_INPUT`, Q12 : 607 caractères
 *   acceptés, 1 115 refusés).
 * - Plusieurs étapes de ce genre dans un MÊME paragraphe sont acceptées quand
 *   leurs plages sont disjointes, même si elles se touchent ; un chevauchement,
 *   ou une étape de bloc sur le même paragraphe, fait refuser le plan
 *   (`TARGET_CONFLICT`, Q3, Q5). 28 passages en un plan (Q6), 144 étapes en un
 *   plan (Q9) : une entrée d'historique, aucune révision.
 * - L'aperçu rend, pour chaque étape, la plage et le TEXTE que l'éditeur vise
 *   (`resolutions`, Q1), au-delà de cent étapes aussi (Q9). Pour une étape de
 *   BLOC, c'est le texte entier du paragraphe tel que l'éditeur le compte (Q15).
 * - Le texte d'un paragraphe est le même pour l'éditeur et pour le fichier
 *   quand il porte une tabulation, un saut de ligne, un champ simple ou
 *   complexe, un lien, un signet, une insertion ou une suppression en attente,
 *   un émoji, un `w:sym`, une césure optionnelle, une image en ligne, un
 *   contrôle de contenu, une balise active, un commentaire ancré, un `w:t` sans
 *   `xml:space` (Q14, Q15). Il DIFFÈRE quand il porte un appel de note de bas
 *   de page ou de fin, que l'éditeur compte pour un caractère, ou un tiret
 *   insécable, qu'il ne compte pas (Q15).
 * - ⚠️ Le texte d'un PASSAGE ne prouve pas sa position : derrière un tiret
 *   insécable, un passage uniforme (« ____ » suivi de « ____ ») se résout à la
 *   même plage et au même texte, un caractère plus loin (Q17).
 * - Un paragraphe à appel de note refuse toute étape par texte
 *   (`REVISION_MISMATCH` à la compilation de l'aperçu, Q13), alors qu'une étape
 *   de bloc y passe et s'applique (Q13, Q18). Un aperçu qui porte plusieurs
 *   étapes refusées les nomme TOUTES en une passe, rend `valid:false`, et rend
 *   quand même les étapes valides (Q16).
 * - Le texte en suppression suivie (`w:del`) n'est dans le texte ni du lecteur
 *   ni de l'éditeur (Q15), et une écriture ne le touche pas : sur un paragraphe
 *   gras d'un bout à l'autre qui porte une suppression en attente non grasse,
 *   une étape de bloc, puis une étape par position couvrant tout le texte
 *   visible, laissent les propriétés de caractère du texte supprimé telles
 *   qu'elles étaient au fichier, sans révision (Q19, Q6). Un déplacement en
 *   attente (`w:moveFrom`) n'a pas été mesuré.
 * - Une étape par position qui ne change rien fait refuser le plan (`NO_OP`)
 *   comme une étape de bloc, avec les mêmes effets sur la révision (Q8) ; une
 *   plage dont une PART seulement porte déjà la valeur est acceptée. Le
 *   « sans effet » se prédit donc au grain du passage (`ecritureSurLEtendue`).
 *
 * Ce que le code en fait (décisions pures dans `planDeFormatText.ts`) :
 *
 * 1. Un passage qui est TOUT le texte de son paragraphe s'écrit par une étape
 *    de bloc, qui n'a besoin d'aucune position : il n'est jamais écarté.
 * 2. Pour les autres, un aperçu de LECTURE — des étapes de bloc, jamais
 *    appliquées — rend le texte de chaque paragraphe concerné. Un paragraphe
 *    dont l'éditeur ne rend pas, caractère pour caractère, le texte du fichier
 *    n'est écrit nulle part par position : ses passages sont écartés et comptés
 *    avec leur raison (`format.onlyWhere.passagesSetAside`). Cet écart est
 *    décidé avant l'aperçu du plan : il ne dépend d'aucun tour d'aperçu, quel
 *    que soit le nombre de paragraphes à appel de note. Un aperçu de lecture
 *    que l'éditeur jette, refuse ou rend illisible n'écarte rien : l'appel est
 *    refusé avec sa cause, comme au point 3 — elle peut être passagère.
 * 3. L'aperçu du plan. Refusé par l'éditeur : l'appel est refusé avec la cause
 *    que l'éditeur donne, et rien n'est écarté pour lui. Valide : chaque étape
 *    par position doit y viser, d'UNE plage, le paragraphe, la plage et le
 *    texte du fichier ; sinon elle est écartée (`position_not_proven`) et le
 *    plan aperçu de nouveau, `TOURS_DE_PREUVE_MAX` fois au plus.
 *
 * Ce qui est prouvé : une étape par position n'est écrite que dans un
 * paragraphe dont l'éditeur rend le texte du fichier. Ce qui ne l'est pas : que
 * deux textes égaux se comptent de la même façon pour un élément que la sonde
 * n'a pas mesuré. La relecture du fichier juge ensuite chaque passage trouvé —
 * écrit à côté, il sortirait « différent » ; le texte voisin, lui, n'est pas relu.
 *
 * Ce que cela coûte (`tests/ws-mocked/mesure-format-text-only-where-cout.spec.ts`,
 * 2026-10-02, acte synthétique de 1 700 paragraphes dont 810 de texte, deux
 * passages gras chacun, cible « tout l'acte ») : 5 aperçus de lecture, 9
 * aperçus du plan, 9 applications pour 1 620 passages ; 187 s puis 217 s de
 * l'enveloppe au reçu, sur deux passes. Sur un lot de 200 étapes, l'aperçu de
 * lecture prend 26 ms, l'aperçu du plan 87 ms, l'application 11,7 s : c'est
 * l'application des étapes par position qui coûte, pas les preuves. Le reste
 * de la durée n'a pas été réparti. La borne d'attente du courtier backend pour
 * ce verbe est dimensionnée sur ce relevé (`BORNES_DES_VERBES_DE_FORME_MS`,
 * `SuperDocCanvasTools.ts`) : ⚠️ un filtre qui trouve nettement plus de
 * passages que la sonde expire côté backend pendant que le navigateur écrit
 * encore.
 *
 * ## Un texte exact dans tout l'acte (#1947)
 *
 * Une cible « texte » sans paragraphe se résout en la liste de ses occurrences
 * (`occurrencesDansLActe`, `planDeFormatText.ts`) : des passages situés par
 * leur position, comme ceux d'un filtre. Tout ce qui précède vaut donc pour
 * elles sans rien de plus — adressage par position, preuve par l'aperçu, lots,
 * repli, relecture — et un filtre `onlyWhere` s'y applique occurrence par
 * occurrence (`passagesVises`). Conséquence de l'adressage par position : une
 * occurrence dans un paragraphe à appel de note ou à tiret insécable est
 * écartée et nommée, sauf si elle est tout le texte du paragraphe. Une
 * occurrence est un MOT ENTIER, cherché dans le corps de l'acte et ses
 * tableaux (`occurrencesDansLActe`). Avec un paragraphe, la cible « texte »
 * garde son chemin d'origine : UNE occurrence exigée, suite de caractères
 * adressée par son texte.
 *
 * **Plusieurs textes en un appel.** Mesuré dans l'application le 2026-10-05
 * (acte de 180 paragraphes, vrai agent) : « mets en rouge foncé toutes les
 * dates » coûtait 22 appels `format_text`, un par date, et 332 s ; un texte par
 * appel, c'est un appel au modèle par texte. La cible prend donc une liste de
 * textes, résolue au MÊME endroit (`occurrencesDansLActe`) : les occurrences de
 * tous les textes forment une seule liste de passages, dans l'ordre du
 * document, et rien de la suite ne sait de combien de textes elle vient. Deux
 * règles propres à la liste, décidées là-bas : là où deux textes se
 * chevauchent, le plus long reste (deux étapes qui se chevauchent font refuser
 * le plan, `TARGET_CONFLICT`) ; un texte sans occurrence n'annule pas les
 * autres — il est nommé par le reçu (`format.everywhere.texts`), qui sort
 * `partial`. Seule une liste dont AUCUN texte n'a d'occurrence est refusée
 * (`refusSansOccurrence`).
 *
 * ## Quand l'éditeur refuse une étape
 *
 * Ce que l'éditeur refuse — à l'aperçu ou à l'application — et ce que le socle
 * en fait est dit dans l'en-tête de `ecritureDeForme.ts`. Ici : une étape
 * refusée est un PARAGRAPHE sauté (`format.skipped`, nommé avec sa raison) ou,
 * quand l'écriture vise des passages, un PASSAGE écarté
 * (`format.onlyWhere.passagesSetAside`, `format.everywhere.occurrencesSetAside`) ; une
 * étape que l'éditeur dit sans effet compte comme déjà en forme, et la
 * relecture la juge. Une cible « passage » dont l'unique étape est refusée ne
 * laisse rien à écrire : l'appel est refusé avec la raison de l'éditeur.
 *
 * ## Hors périmètre
 *
 * Dans un en-tête, un pied ou une note, le texte d'une cellule de tableau n'est
 * pas écrit (compté `table_cell`) ; dans le corps, une cellule s'écrit comme un
 * autre paragraphe (§16 de `superdoc-document-api.md`), « tout l'acte » et une
 * plage compris — le reçu compte celles qu'il a écrites
 * (`format.tableCellParagraphsWritten`). Sous un filtre, dans un paragraphe à appel de note ou à tiret insécable, seul un
 * passage qui est tout le texte du paragraphe est écrit ; les autres sont
 * comptés, leurs paragraphes nommés. Un paragraphe où un signet couvre une part
 * du texte est sauté en entier : l'écrire par morceaux de part et d'autre du
 * signet n'est pas fait (`mesure-repli-de-lot`, L8 : le morceau qui suit le
 * signet est lui-même refusé).
 */

import type { CanvasActionInput, CanvasFormatPlace, CanvasFormatWriteReceipt, CanvasFormatWriteSkippedReason } from '@hector/shared-types';
import { CANVAS_TEXT_FORMAT_SETTING_KEYS, lieuDeLaCible } from '@hector/shared-types';
import type { CanvasActionReceipt } from './actionReceipt';
import { resoudreLaCibleDeParagraphes, type ParagrapheCible } from './cibleDeParagraphes';
import { ouvrirLEcritureDeForme, type Refus } from './ecritureDeForme';
import { direLeLieu } from './horsDuCorps';
import type { ActeVise } from '@hector/docx-forme';
import {
  etapeDuPlan,
  etapesAEcrire,
  lireLesTextes,
  occurrencesDansLActe,
  passagesVises,
  prouverLePlan,
  refusDesPositionsNonProuvees,
  refusSansOccurrence,
  situerLesPassages,
  texteDuParagraphe,
  type EtapeAEcrire,
  type EtapeDePlan,
  type EtapeSautee,
  type PassageVise,
} from './planDeFormatText';
import {
  borne,
  citer,
  decrireLesNonResolus,
  direLesHorsDePortee,
  nommer,
  recuDeLEcritureDeForme,
  type CibleResolue,
  type OrigineDeLaSelection,
  type RefusDeLEditeur,
  type TexteVisePartout,
} from './recuDEcritureDeForme';
import { isolerLesEtapes, raisonDuRefus, risqueUnRefusDeTexte } from './repliDeLot';
import { normaliserLaDemande } from './reglagesDeTexte';
import type { CanvasActionContext } from './textActionExecutors';
import { occurrencesDuPassage, type ReglagesDeTexte } from './verificationDeForme';

type FormatTextInput = Extract<CanvasActionInput, { action: 'format_text' }>;

const INTENTION = 'format_text';


/**
 * La cible prête à écrire : ses paragraphes gardent leur forme lue et leur
 * rang. `partout` : présent pour un ou plusieurs textes visés dans tout l'acte —
 * leurs occurrences du corps (les passages à viser), et ce que le reçu en redit.
 */
type CibleAEcrire = Omit<CibleResolue, 'paragraphes'> & {
  paragraphes: ParagrapheCible[];
  partout?: { occurrences: PassageVise[]; dit: TexteVisePartout };
};

/**
 * D'où viennent les passages que l'écriture vise, tel que le reçu le redit.
 * `undefined` : l'écriture vise des paragraphes, ou l'unique passage d'un paragraphe.
 */
function origineDeLaSelection(cible: CibleAEcrire, filtre: FormatTextInput['onlyWhere']): OrigineDeLaSelection | undefined {
  if (cible.partout === undefined) return filtre === undefined ? undefined : { par: 'filtre', filtre };
  const partout = cible.partout.dit;
  return filtre === undefined ? { par: 'partout', partout } : { par: 'partout_et_filtre', partout, filtre };
}

/**
 * Un ou plusieurs textes exacts visés dans tout l'acte : leurs occurrences du
 * corps à écrire, réunies en une liste ; celles hors de portée comptées et
 * nommées. Une liste réduite à UN texte distinct est ce texte : même reçu.
 */
function resoudreLesTextesPartout(acte: ActeVise, textes: readonly string[], lieu: CanvasFormatPlace | undefined): CibleAEcrire | Refus {
  const releve = occurrencesDansLActe(acte, textes);
  const { trouvees, cellules, sansIdentifiant, enDouble, horsDePortee, parTexte } = releve;
  if (trouvees.length === 0) return refusSansOccurrence(releve, lieu);
  const cellulesNommees = cellules.filter((nodeId): nodeId is string => nodeId !== null);
  const omis: NonNullable<CanvasFormatWriteReceipt['skipped']> = [
    // Une cellule sans identifiant au fichier est comptée, faute de nom à donner.
    ...(cellules.length === 0 ? [] : [{ reason: 'table_cell' as const, count: cellules.length, ...(cellulesNommees.length === 0 ? {} : nommer(cellulesNommees)) }]),
    ...(sansIdentifiant === 0 ? [] : [{ reason: 'no_node_id' as const, count: sansIdentifiant }]),
    ...(enDouble === 0 ? [] : [{ reason: 'duplicate_node_id' as const, count: enDouble }]),
  ];
  const [seul, ...autres] = parTexte;
  return {
    paragraphes: [...new Set(trouvees.map(({ paragraphe }) => paragraphe))],
    partout: {
      occurrences: trouvees,
      dit: { occurrences: trouvees.length, horsDePortee, ...(seul !== undefined && autres.length === 0 ? { texte: seul.texte } : { textes: parTexte }) },
    },
    omis,
    nonResolus: [],
  };
}

/** Les réglages demandés, sans la cible ni le verbe, sous la forme que rend le lecteur de forme. */
function reglagesDemandes(input: FormatTextInput): ReglagesDeTexte {
  const reglages: ReglagesDeTexte = {};
  for (const reglage of CANVAS_TEXT_FORMAT_SETTING_KEYS) {
    const valeur = input[reglage];
    if (valeur !== undefined) reglages[reglage] = normaliserLaDemande(reglage, valeur);
  }
  return reglages;
}

/**
 * Résout la cible contre l'acte lu, ou rend le refus à dire à l'agent.
 *
 * @param acte L'acte où la cible se résout : le corps, ou les paragraphes du lieu qu'elle vise (`horsDuCorps.ts`).
 * @param siLaCibleEstVide Dans un lieu, ce que le refus conseille quand elle n'y désigne aucun texte (`EcritureDeFormeOuverte`).
 */
function resoudreLaCible(acte: ActeVise, cible: FormatTextInput['target'], siLaCibleEstVide: string | undefined): CibleAEcrire | Refus {
  const lieu = lieuDeLaCible(cible);
  if (cible.kind === 'text') {
    if (cible.nodeId === undefined) return resoudreLesTextesPartout(acte, typeof cible.text === 'string' ? [cible.text] : cible.text, lieu);
    const passage = cible.text;
    // Le contrat refuse déjà une liste avec un paragraphe (`appliquerLesReglesInterChamps`, `actionCatalog.ts`) : même refus, si elle arrive ici.
    if (typeof passage !== 'string') {
      return {
        code: 'FORMAT_TARGET_INVALID',
        message: `format_text was refused before any write: a list of texts (got ${passage.length}) designates their occurrences in the whole act and takes no nodeId (got ${JSON.stringify(cible.nodeId)}). `
          + 'Nothing was written. Remove nodeId, or send one text with this nodeId.',
      };
    }
    const { paragraphes, nonResolus } = resoudreLaCibleDeParagraphes(acte, 'cellules_couvertes', { kind: 'ids', nodeIds: [cible.nodeId] });
    const paragraphe = paragraphes[0];
    if (paragraphe === undefined) {
      return {
        code: 'FORMAT_TARGET_NOT_FOUND',
        message: `format_text was refused before any write: target paragraph ${decrireLesNonResolus(nonResolus)}. `
          + 'Nothing was written. Use a nodeId published by a read of this document.',
      };
    }
    const occurrences = occurrencesDuPassage(texteDuParagraphe(paragraphe), passage);
    if (occurrences !== 1) {
      return {
        code: occurrences === 0 ? 'FORMAT_PASSAGE_NOT_FOUND' : 'FORMAT_PASSAGE_AMBIGUOUS',
        reessayer: true,
        message: `format_text was refused before any write: paragraph ${cible.nodeId} contains ${occurrences} occurrence(s) of `
          + `${citer(passage)}, expected exactly 1 (case-sensitive, plain text as displayed). `
          + 'Nothing was written. '
          + (occurrences === 0
            ? 'Copy the passage exactly as the paragraph reads, or target the whole paragraph with {kind:"ids"}.'
            : 'Lengthen the passage with the words around it until it occurs once in that paragraph.'),
      };
    }
    return { paragraphes: [paragraphe], passage, omis: [], nonResolus: [] };
  }

  const resolue = resoudreLaCibleDeParagraphes(acte, 'cellules_couvertes', cible);
  if (cible.kind === 'range' && resolue.nonResolus.length > 0) {
    return {
      code: 'FORMAT_TARGET_NOT_FOUND',
      message: `format_text was refused before any write: range bound ${decrireLesNonResolus(resolue.nonResolus)}. `
        + 'Nothing was written: a range with a missing bound is never guessed. Use two nodeIds of paragraphs published by a read of this document (table cells included).',
    };
  }
  const avecTexte = resolue.paragraphes.filter((paragraphe) => paragraphe.passages.length > 0);
  const sansTexte = resolue.paragraphes.length - avecTexte.length;
  const omis: Array<{ reason: CanvasFormatWriteSkippedReason; count: number }> = [
    ...resolue.omis,
    ...(sansTexte === 0 ? [] : [{ reason: 'no_text' as const, count: sansTexte }]),
  ];
  if (avecTexte.length === 0) {
    const couvert = [
      ...omis.map(({ reason, count }) => `${count} ${reason}`),
      ...(resolue.nonResolus.length === 0 ? [] : [`unresolved: ${decrireLesNonResolus(resolue.nonResolus)}`]),
    ];
    return {
      code: 'FORMAT_TARGET_EMPTY',
      message: `format_text was refused before any write: the target designates no paragraph with text in ${direLeLieu(lieu)}`
        + `${couvert.length === 0 ? '' : ` (${couvert.join('; ')})`}. Nothing was written. `
        + 'Paragraphs without text have no text to format. '
        + (direLesHorsDePortee('format_text', omis, lieu) ?? siLaCibleEstVide ?? 'Target paragraphs that carry text.'),
    };
  }
  return {
    // Une liste est rendue dans l'ordre demandé : le plan, lui, suit l'ordre du document.
    paragraphes: [...avecTexte].sort((a, b) => a.rang - b.rang),
    omis,
    nonResolus: resolue.nonResolus,
    cellules: resolue.cellules,
  };
}

/**
 * Met en forme le texte que la cible désigne, en écriture directe, par plans
 * atomiques, puis relit le fichier exporté pour dire ce qui est réellement posé.
 *
 * Refusé avant toute écriture (`aborted`, cause nommée) tant que la cible, la
 * lecture du fichier ou l'aperçu d'un plan fait défaut ; ensuite, le reçu est
 * décidé par `recuDeLEcritureDeForme` sur les faits relevés ici.
 */
export async function executeFormatText(ctx: CanvasActionContext, input: FormatTextInput): Promise<CanvasActionReceipt> {
  const reglages = reglagesDemandes(input);
  const ecriture = await ouvrirLEcritureDeForme(ctx, INTENTION, {}, lieuDeLaCible(input.target));
  if ('refus' in ecriture) return ecriture.refus;
  const { acte, refuser } = ecriture;

  const cible = resoudreLaCible(acte, input.target, ecriture.siLaCibleEstVide);
  if ('code' in cible) return refuser(cible);

  // Sous un filtre, ou pour un texte visé dans tout l'acte, la cible à écrire n'est plus faite de paragraphes mais de passages
  // (`vises`) ; `origine` dit au reçu d'où ils viennent.
  const filtre = input.onlyWhere;
  const origine = origineDeLaSelection(cible, filtre);
  const vises = passagesVises({ paragraphes: cible.paragraphes, passage: cible.passage, occurrences: cible.partout?.occurrences }, filtre);
  const candidats = etapesAEcrire(acte, cible.paragraphes, cible.passage, reglages, vises?.trouves);

  // Aperçoit un lot : les étapes que l'éditeur accepte et celles qu'il refuse, ou le refus de l'appel (`ecritureDeForme.ts`).
  const apercevoir = (steps: EtapeDePlan[]) => ecriture.apercevoir(steps, 'formatting');

  // ── Quand l'écriture vise des passages : le texte de chaque paragraphe où un passage s'adresse par position est lu dans
  // l'éditeur. Un paragraphe dont le texte n'est pas celui du fichier n'est écrit nulle part par position
  // (voir l'en-tête) : ses passages sont écartés et comptés. C'est un aperçu, rien n'est écrit ; s'il
  // échoue, l'appel est refusé avec sa cause, comme pour l'aperçu du plan.
  const lecture = await lireLesTextes(candidats, apercevoir);
  if ('refus' in lecture) return lecture.refus;
  const situes = situerLesPassages(candidats, lecture.textes);

  // ── Aperçu de TOUS les lots avant la première écriture : une étape que l'éditeur
  // refuserait est connue ici, document intact, et retirée du plan.
  // L'aperçu est aussi la seconde preuve de chaque étape par position : l'éditeur
  // doit y viser le paragraphe, la plage et le texte que le fichier donne.
  const prouve = await prouverLePlan(situes.aEcrire, cible.passage, apercevoir);
  if ('refus' in prouve) return prouve.refus;
  if ('nonProuvees' in prouve) return refuser(refusDesPositionsNonProuvees(prouve.nonProuvees, origine));
  const { etapes } = prouve;
  // Les étapes que l'éditeur risque de refuser à l'application partent seules, avant les lots (`repliDeLot.ts`).
  const aRisque = (step: EtapeDePlan): boolean => risqueUnRefusDeTexte(etapeDuPlan(etapes, step.id)?.paragraphe.porte ?? []);
  const lots = isolerLesEtapes(prouve.lots, aRisque);

  // Après un plan refusé qui a joué une étape, la révision a bougé : les passages adressés par position sont
  // re-prouvés sur le texte entier de leur paragraphe, lu de nouveau dans l'éditeur. Un texte qui n'est plus
  // celui du fichier interrompt la série — l'avocat a tapé dans ce paragraphe pendant l'appel.
  const reprouver = async (restantes: readonly EtapeDePlan[]): Promise<RefusDeLEditeur | undefined> => {
    const parPosition = restantes.flatMap((step) => etapeDuPlan(etapes, step.id) ?? []).filter((etape) => etape.plage !== undefined && etape.parBloc !== true);
    if (parPosition.length === 0) return undefined;
    const relue = await lireLesTextes(parPosition, apercevoir);
    if ('refus' in relue) {
      return { code: 'STALE_CANVAS_REVISION', detail: `STALE_CANVAS_REVISION: after the editor refused a batch, the text of the paragraphs still to write could not be read again (${borne(relue.refus.errors[0]?.message ?? 'no cause given', 120)})` };
    }
    const changes = [...new Set(situerLesPassages(parPosition, relue.textes).ecartes.map(({ paragraphe }) => paragraphe.nodeId))];
    return changes.length === 0 ? undefined : {
      code: 'STALE_CANVAS_REVISION',
      detail: `STALE_CANVAS_REVISION: after the editor refused a batch, the text of paragraph(s) ${changes.slice(0, 5).join(', ')} is no longer the one read from the document file `
        + '(the lawyer typed there during the call), so the passages left to write there were not written',
    };
  };

  // ── Écriture : un plan atomique direct par lot, puis la relecture clôturée (`ecritureDeForme.ts`).
  const sorts = await ecriture.appliquerLesPlans(lots, 'plan', { reprouver });
  const etapesDe = (stepIds: readonly string[]): EtapeAEcrire[] => stepIds.flatMap((stepId) => etapeDuPlan(etapes, stepId) ?? []);
  const ecrites = new Set(sorts.flatMap((sort) => etapesDe(sort.ecrites)));
  const sansObjet = new Set(sorts.flatMap((sort) => etapesDe(sort.sansObjet)));
  const sautees: EtapeSautee[] = [
    ...prouve.sautees,
    ...sorts.flatMap((sort) => sort.sautees.flatMap(({ stepId, refus }) => etapesDe([stepId]).map((etape) => ({ etape, refus })))),
  ];
  // Les étapes soumises seules que l'éditeur a écrites : chacune est sa propre entrée d'historique.
  const ecritesSeules = lots.filter((lot, rang) => lot.length === 1 && lot.every(aRisque) && (sorts[rang]?.ecrites.length ?? 0) > 0).length;
  // Ce que l'éditeur refuse d'écrire, avec la raison à redire : un paragraphe, ou un passage quand l'écriture en vise.
  const refusees = sautees
    .map(({ etape, refus }) => ({ etape, raison: raisonDuRefus(refus, etape.paragraphe.porte) }))
    .sort((a, b) => a.etape.paragraphe.rang - b.etape.paragraphe.rang || (a.etape.plage?.debut ?? 0) - (b.etape.plage?.debut ?? 0));
  const nonEcrites = new Set(sautees.map(({ etape }) => etape));
  // Une étape que l'éditeur dit sans effet n'était pas à écrire : elle compte comme déjà en forme, et la relecture la juge.
  const restantes = prouve.aEcrire.filter((etape) => !nonEcrites.has(etape) && !sansObjet.has(etape));
  // Les étapes écrites en tête, comme le reçu les attend ; les autres n'ont pas été soumises (écriture interrompue).
  const aEcrire = [...restantes.filter((etape) => ecrites.has(etape)), ...restantes.filter((etape) => !ecrites.has(etape))];
  const paragraphesSautes = new Set(vises === undefined ? refusees.map(({ etape }) => etape.paragraphe.nodeId) : []);

  return recuDeLEcritureDeForme({
    verbe: INTENTION,
    reglages,
    // Un paragraphe sauté n'est ni écrit ni relu : il sort de ce que le reçu juge.
    cible: {
      paragraphes: cible.paragraphes.filter((paragraphe) => !paragraphesSautes.has(paragraphe.nodeId)),
      ...(cible.passage === undefined ? {} : { passage: cible.passage }),
      omis: cible.omis,
      nonResolus: cible.nonResolus,
      ...(cible.cellules === undefined ? {} : { cellules: cible.cellules }),
    },
    ...(vises === undefined || origine === undefined
      ? { sautes: refusees.map(({ etape, raison }) => ({ nodeId: etape.paragraphe.nodeId, ...raison })) }
      : {
          selection: {
            ...origine,
            trouves: vises.trouves.map(({ paragraphe, plage }) => ({ nodeId: paragraphe.nodeId, plage })),
            ecartes: [
              ...[...situes.ecartes, ...prouve.ecartes].map(({ paragraphe, plage, raison }) => ({ nodeId: paragraphe.nodeId, plage, raison })),
              ...refusees.flatMap(({ etape, raison: { reason, detail } }) => (
                etape.plage === undefined ? [] : [{ nodeId: etape.paragraphe.nodeId, plage: etape.plage, raison: reason, ...(detail === undefined ? {} : { detail }) }]
              )),
            ],
            nonEvalues: vises.nonEvalues,
            reglagesNonLus: vises.reglagesNonLus,
          },
        }),
    aEcrire: aEcrire.map(({ paragraphe, plage }) => (plage === undefined ? paragraphe : { nodeId: paragraphe.nodeId, plage })),
    ecrituresPrevues: lots.length,
    etapesEcrites: ecrites.size,
    ...(ecritesSeules === 0 ? {} : { ecritesSeules }),
    ...(await ecriture.clore()),
  });
}
